import type { Lang } from "@/i18n";

export const BLOG_CATEGORIES = [
  { value: "mikrobiom", label: "Mikrobiom", labelEn: "Microbiome" },
  { value: "ernaehrung", label: "Ernährung", labelEn: "Nutrition" },
  { value: "geist_seele", label: "Geist & Seele", labelEn: "Mind & Soul" },
  {
    value: "naturheilkunde_mikronaehrstoffe",
    label: "Naturheilkunde & Mikronährstoffe",
    labelEn: "Naturopathy & Micronutrients",
  },
  { value: "allgemeine_gesundheit", label: "Allgemeine Gesundheit", labelEn: "General Health" },
] as const;

export type BlogCategory = (typeof BLOG_CATEGORIES)[number]["value"];

export const AUTHOR = {
  name: "Isabella Bartak",
  jobTitle: "akad. dipl. zert. komplementärmedizinische Therapeutin",
  jobTitleEn: "certified complementary medicine therapist (academic degree)",
  url: "https://ibpraxis.net/#about",
} as const;

export const categoryLabel = (value: string, lang: Lang = "de"): string => {
  const cat = BLOG_CATEGORIES.find((c) => c.value === value);
  if (!cat) return value;
  return lang === "en" ? cat.labelEn : cat.label;
};

export const slugify = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/** Raw post row shape with optional English columns. */
export interface LocalizablePost {
  title: string;
  slug: string;
  excerpt?: string | null;
  content?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  meta_keywords?: string | null;
  cover_image_alt?: string | null;
  title_en?: string | null;
  slug_en?: string | null;
  excerpt_en?: string | null;
  content_en?: string | null;
  meta_title_en?: string | null;
  meta_description_en?: string | null;
  meta_keywords_en?: string | null;
  cover_image_alt_en?: string | null;
}

/** Pick the fields of the active language, falling back to German. */
export const localizePost = <T extends LocalizablePost>(post: T, lang: Lang) => {
  const en = lang === "en";
  return {
    title: (en && post.title_en) || post.title,
    slug: (en && post.slug_en) || post.slug,
    excerpt: (en ? post.excerpt_en : null) ?? post.excerpt ?? null,
    content: (en ? post.content_en : null) ?? post.content ?? "",
    metaTitle: (en ? post.meta_title_en : null) ?? post.meta_title ?? null,
    metaDescription: (en ? post.meta_description_en : null) ?? post.meta_description ?? null,
    metaKeywords: (en ? post.meta_keywords_en : null) ?? post.meta_keywords ?? null,
    coverImageAlt: (en ? post.cover_image_alt_en : null) ?? post.cover_image_alt ?? null,
    /** true when a real English version exists */
    hasEnglish: Boolean(post.title_en && post.content_en),
  };
};

export const postDateLocale = (lang: Lang) => (lang === "en" ? "en-GB" : "de-DE");

export const formatPostDate = (iso: string | null, lang: Lang) =>
  iso
    ? new Date(iso).toLocaleDateString(postDateLocale(lang), {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "";
